import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Plus, MoreVertical, Trash2, X, Pencil, ArrowLeft } from 'lucide-react';
import AlertsTable from './AlertsTable';
import CreateAlertModal from './modals/CreateAlertModal';
import EditAlertModal from './modals/EditAlertModal';
import FilterModal from './modals/FilterModal';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import StatusMessage, { StatusType } from '../../components/StatusMessage';
import { useAlerts } from '../../hooks/useAlerts';
import { AlertData } from '../../services/alertApi';
import { SimplifiedCreateAlertRequest, ApiAlertData } from '../../types/alerts';
import { DevicesContext } from '../../contexts/DevicesContext';
import { UserSelectedContext } from '../../contexts/UserSelectedContext';
import '../../styles/pages/newAlerts.css';
import DashboardLayout from '../../components/DashboardLayout';

type TabType = 'All' | 'Critical' | 'Warning' | 'Resolved';
type NotificationSeverity = 'Critical' | 'Warning' | 'Resolved';

const getTimeAgo = (dateStr: string): string => {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
};

const getSeverity = (alert: ApiAlertData | AlertData): NotificationSeverity => {
  if (!alert.isActive) return 'Resolved';
  if (!alert.lastTriggeredAt) return 'Warning';
  const diffHours = (new Date().getTime() - new Date(alert.lastTriggeredAt).getTime()) / 3600000;
  return diffHours < 1 ? 'Critical' : 'Warning';
};

const NewAlertsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    alerts,
    loading,
    error,
    createSimplifiedAlert,
    updateSimplifiedAlert,
    deleteAlert,
    toggleAlertStatus,
    clearError,
  } = useAlerts();

  const devicesContext = useContext(DevicesContext);
  if (!devicesContext) {
    throw new Error('useDevices must be used within a DevicesProvider');
  }
  const { devices, getUserDevices } = devicesContext;

  const userSelectedContext = useContext(UserSelectedContext);
  if (!userSelectedContext) {
    throw new Error('useUserSelected must be used within a UserSelectedProvider');
  }
  const { hideBottomBar, showBottomBar, isBottomBarHidden } = userSelectedContext;

  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<ApiAlertData | null>(null);
  const [alertToDelete, setAlertToDelete] = useState<ApiAlertData | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [filters, setFilters] = useState({ device: '', status: '', date: '' });
  const [screenSize, setScreenSize] = useState(window.innerWidth);
  const [showActionMenu, setShowActionMenu] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('All');

  const [statusMessage, setStatusMessage] = useState<{
    type: StatusType;
    message: string;
    isVisible: boolean;
  }>({ type: 'success', message: '', isVisible: false });

  useEffect(() => {
    if (error) {
      showStatusMessage('error', `Error: ${error}`);
      clearError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (devices.length === 0) getUserDevices();
  }, []);

  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (screenSize <= 768 && isBottomBarHidden && !(isCreateModalOpen || isEditModalOpen)) {
      showBottomBar();
    }
  }, [isCreateModalOpen, isEditModalOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showActionMenu &&
        !(event.target as Element).closest('.na-mobile-action-menu') &&
        !(event.target as Element).closest('.mobile-action-menu-button')
      ) {
        setShowActionMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showActionMenu]);

  const showStatusMessage = (type: StatusType, message: string) => {
    setStatusMessage({ type, message, isVisible: true });
  };

  const closeStatusMessage = () => {
    setStatusMessage(prev => ({ ...prev, isVisible: false }));
  };

  const filteredAlerts = useMemo(
    () =>
      (alerts || []).filter(alert => {
        if (
          searchTerm &&
          !(
            alert.alertName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            alert.deviceName.toLowerCase().includes(searchTerm.toLowerCase())
          )
        )
          return false;
        if (filters.device !== 'All Devices' && filters.device && alert.deviceName !== filters.device)
          return false;
        if (filters.status !== 'All Statuses' && filters.status) {
          if ((alert.isActive ? 'active' : 'inactive') !== filters.status) return false;
        }
        if (filters.date && alert.lastTriggeredAt) {
          if (
            new Date(filters.date).toDateString() !== new Date(alert.lastTriggeredAt).toDateString()
          )
            return false;
        }
        return true;
      }),
    [alerts, searchTerm, filters]
  );

  // Computed overview stats
  const stats = useMemo(() => {
    const data = alerts || [];
    const now = new Date();
    const today = now.toDateString();

    const critical = data.filter(a => {
      if (!a.isActive || !a.lastTriggeredAt) return false;
      return (now.getTime() - new Date(a.lastTriggeredAt).getTime()) / 3600000 < 1;
    }).length;

    const resolvedToday = data.filter(
      a => !a.isActive && a.lastTriggeredAt && new Date(a.lastTriggeredAt).toDateString() === today
    ).length;

    const warnings = data.filter(a => {
      if (!a.isActive) return false;
      if (!a.lastTriggeredAt) return true;
      return (now.getTime() - new Date(a.lastTriggeredAt).getTime()) / 3600000 >= 1;
    }).length;

    const infoNotices = data.filter(a => !a.lastTriggeredAt).length;

    return { critical, resolvedToday, warnings, infoNotices };
  }, [alerts]);

  // Notification feed
  const allNotifications = useMemo(
    () =>
      (alerts || [])
        .map(a => ({ ...a, severity: getSeverity(a) }))
        .sort((a, b) => {
          const at = a.lastTriggeredAt ? new Date(a.lastTriggeredAt).getTime() : 0;
          const bt = b.lastTriggeredAt ? new Date(b.lastTriggeredAt).getTime() : 0;
          return bt - at;
        }),
    [alerts]
  );

  const filteredNotifications = useMemo(() => {
    const list =
      activeTab === 'All'
        ? allNotifications
        : allNotifications.filter(n => n.severity === activeTab);
    return list.slice(0, 5);
  }, [allNotifications, activeTab]);

  const handleCreateAlert = async (newAlert: SimplifiedCreateAlertRequest) => {
    try {
      const response = await createSimplifiedAlert(newAlert);
      if (response) {
        setIsCreateModalOpen(false);
        showStatusMessage('success', `Alert "${newAlert.alertName}" created successfully!`);
      } else {
        showStatusMessage('error', 'Failed to create alert. Please try again.');
      }
    } catch {
      showStatusMessage('error', 'Error creating alert. Please try again.');
    }
  };

  const handleEditAlert = async (updatedAlert: SimplifiedCreateAlertRequest) => {
    if (!selectedAlert) return;
    try {
      const response = await updateSimplifiedAlert(selectedAlert.alertId, updatedAlert);
      if (response) {
        setIsEditModalOpen(false);
        setSelectedAlert(null);
        showStatusMessage('success', `Alert "${updatedAlert.alertName}" updated successfully!`);
      } else {
        showStatusMessage('error', 'Failed to update alert. Please try again.');
      }
    } catch {
      showStatusMessage('error', 'Error updating alert. Please try again.');
    }
  };

  const handleDeleteClick = (alert: AlertData) => {
    const apiAlert = (alerts || []).find(a => a.alertId === alert.alertId);
    if (apiAlert) {
      setAlertToDelete(apiAlert as any);
      setIsDeleteModalOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!alertToDelete) return;
    setIsDeleting(true);
    try {
      const success = await deleteAlert(alertToDelete.alertId);
      showStatusMessage(
        success ? 'success' : 'error',
        success
          ? `Alert "${alertToDelete.alertName}" deleted successfully!`
          : 'Failed to delete alert. Please try again.'
      );
    } catch {
      showStatusMessage('error', 'Error deleting alert. Please try again.');
    } finally {
      setIsDeleting(false);
      setAlertToDelete(null);
      setIsDeleteModalOpen(false);
    }
  };

  const handleEditClick = (alert: AlertData) => {
    const apiAlert = (alerts || []).find(a => a.alertId === alert.alertId);
    if (apiAlert) {
      setSelectedAlert(apiAlert as any);
      setIsEditModalOpen(true);
    }
  };

  const handleStatusToggle = async (alertId: number) => {
    const result = await toggleAlertStatus(alertId);
    if (result.success) {
      showStatusMessage(
        'success',
        result.isActive ? 'Alert made active successfully!' : 'Alert made Inactive Successfully!'
      );
    } else {
      showStatusMessage('error', 'Failed to update alert status. Please try again.');
    }
  };

  const handleAddAlertClick = () => {
    if (devices.length === 0) {
      showStatusMessage(
        'warning',
        'Please onboard a device first to create an alert. Visit the Devices page to add your first device.'
      );
      return;
    }
    setIsCreateModalOpen(true);
    if (screenSize <= 768) hideBottomBar();
  };

  // Mobile cards
  const renderMobileCards = () => {
    if (filteredAlerts.length === 0) {
      return (
        <div className="mobile-alerts-container">
          <div className="mobile-empty-state">
            <div className="mobile-empty-icon">📋</div>
            <h3 className="mobile-empty-title">No Alerts Found</h3>
            <p className="mobile-empty-description">
              {devices.length > 0
                ? searchTerm || Object.values(filters).some(f => f)
                  ? 'No alerts match your current filters. Try adjusting your search or filters.'
                  : "You haven't created any alerts yet. Create your first alert to get started."
                : "You haven't added any devices yet. Please add a device to start creating alerts."}
            </p>
            {!searchTerm && !Object.values(filters).some(f => f) && (
              <button onClick={handleAddAlertClick} className="mobile-empty-button">
                <Plus className="w-4 h-4" />
                Create First Alert
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="mobile-alerts-container">
        {filteredAlerts.map(alert => (
          <div key={alert.alertId} className="mobile-alert-card">
            <div className="mobile-alert-header">
              <div className="mobile-alert-title-section">
                <h3 className="mobile-alert-name">{alert.alertName}</h3>
                <div className="mobile-alert-status">
                  <div className={`mobile-status-dot ${alert.isActive ? 'active' : 'inactive'}`} />
                  <span className={`mobile-status-text ${alert.isActive ? 'active' : 'inactive'}`}>
                    {alert.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div className="mobile-alert-actions">
                <div className="status-toggle-wrapper">
                  <button className="mobile-status-toggle" onClick={() => handleStatusToggle(alert.alertId)}>
                    <div className={`mobile-toggle-slider ${alert.isActive ? 'active' : 'inactive'}`}>
                      <div className="mobile-toggle-handle" />
                    </div>
                  </button>
                </div>
                <button
                  className="mobile-action-menu-button"
                  onClick={() => setShowActionMenu(showActionMenu === alert.alertId ? null : alert.alertId)}
                >
                  <MoreVertical className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="mobile-alert-content">
              <div className="mobile-device-date-section">
                <span
                  className="mobile-device-name mobile-device-link"
                  onClick={e => {
                    e.stopPropagation();
                    if (alert.deviceId) navigate(`/devices/${alert.deviceId}`);
                  }}
                >
                  {alert.deviceName}
                </span>
                <span className="mobile-alert-date">
                  {alert.lastTriggeredAt ? new Date(alert.lastTriggeredAt).toLocaleString() : '--'}
                </span>
              </div>

              <div className="mobile-readings-section">
                <div className="mobile-readings-grid">
                  {alert.alertConditions?.length > 0 ? (
                    alert.alertConditions.map((condition: any, idx: number) => (
                      <div key={idx} className="mobile-reading-item">
                        <span className="mobile-reading-label">
                          {condition.pollutantParameterName || condition.parameterName || `Parameter ${condition.pollutantParameterId}`}
                        </span>
                        <span className="mobile-reading-operator">{condition.operator}</span>
                        <span className="mobile-reading-value">{condition.thresholdValue}</span>
                        {(condition.parameterUnitName || condition.unitSymbol) && (
                          <span className="mobile-reading-unit">
                            {condition.parameterUnitName || condition.unitSymbol}
                          </span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="mobile-no-conditions">No conditions</div>
                  )}
                </div>
              </div>

              <div className="mobile-recipients-section">
                <span className="mobile-recipients-label">Recipients</span>
                <div className="mobile-recipients-list">
                  {alert.alertRecipients?.length > 0
                    ? alert.alertRecipients.map((r: any) => r.email).join(', ')
                    : <span className="mobile-no-recipients">No recipients</span>}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderMobileActionMenu = () => {
    if (!showActionMenu) return null;
    const menuAlert = filteredAlerts.find(a => a.alertId === showActionMenu);
    if (!menuAlert) return null;

    return (
      <div className="mobile-action-menu-overlay" onClick={() => setShowActionMenu(null)}>
        <div className="na-mobile-action-menu mobile-action-menu">
          <button className="mobile-action-close" onClick={() => setShowActionMenu(null)}>
            <X className="w-5 h-5 text-[var(--color-text)]" />
          </button>
          <div className="mobile-action-content">
            <button
              className="mobile-action-item"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                handleEditClick(menuAlert);
                setShowActionMenu(null);
                hideBottomBar();
              }}
            >
              <Pencil className="w-5 h-5" />
              Edit Details
            </button>
            <button
              className="mobile-action-item delete"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                handleDeleteClick(menuAlert);
                setShowActionMenu(null);
              }}
            >
              <Trash2 className="w-5 h-5" />
              Remove Alert
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div
        className={`alerts-container na-page-bg${
          screenSize <= 768 && (isCreateModalOpen || isEditModalOpen) ? 'isMobileModalOpen' : ''
        }`}
      >
        {/* Loading */}
        {loading && (
          <div className="h-[calc(100dvh-5rem)] loading-container">
            <div className="h-8 w-8 border-b-2 border-[var(--gradient-button-left-color)] mb-4 animate-spin rounded-full" />
            <p className="text-base text-[var(--color-sub-title-text)]">Loading alerts...</p>
          </div>
        )}

        {!loading && (
          <>
            {/* ── Header ────────────────────────────────────────────────── */}
            <div className="alerts-page-header zoomOutLevel">
              {screenSize <= 768 ? (
                <div className="share-link-mobile-header">
                  <div className="share-link-mobile-header-left">
                    <ArrowLeft className="share-link-back-icon" size={24} onClick={() => navigate(-1)} />
                    <h1 className="share-link-mobile-title">Alerts</h1>
                  </div>
                </div>
              ) : (
                <div className="alerts-page-header-left">
                  <h1 className="device-title">{screenSize > 860 ? 'Alerts Management' : 'Alerts'}</h1>
                  <p className="alerts-subtitle device-subtitle ml-0">Configure alerts and monitor active notifications</p>
                </div>
              )}

              <div className="alerts-page-header-right">
                <div className="search-bar-container flex-1 lg:min-w-[400px]">
                  <input
                    type="text"
                    placeholder="Search by alert name"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                  <div className="search-bar-icon">
                    <Search className="w-6 h-6 text-white" />
                  </div>
                </div>

                <button onClick={() => setIsFilterModalOpen(true)} className="alerts-filter-button">
                  {screenSize > 768 && 'Filter'}{' '}
                  <Filter className="w-4 h-4 text-[var(--color-sub-title-text)]" />
                </button>

                <button onClick={handleAddAlertClick} className="alerts-add-button">
                  {screenSize > 768 ? '+ Add New Alert' : <Plus className="w-6 h-6" />}
                </button>

                {screenSize > 768 && (
                  <button className="na-configure-btn">Configure Threshold</button>
                )}
              </div>
            </div>

            {/* ── Overview Section (desktop only) ───────────────────────── */}
            {screenSize > 860 && (
              <div className="na-overview zoomOutLevel">
                {/* Stat cards */}
                <div className="na-overview-left">
                  <p className="na-section-label">OVERALL ALERTS</p>
                  <div className="na-stats-grid">
                    <div className="na-stat-card na-critical">
                      <div className="na-circle na-c1" />
                      <div className="na-circle na-c2" />
                      {/* <div className="na-circle na-c3" /> */}
                      <p className="na-stat-label">Critical</p>
                      <h2 className="na-stat-value">{stats.critical}</h2>
                    </div>

                    <div className="na-stat-card na-resolved">
                      <div className="na-circle na-c1" />
                      <div className="na-circle na-c2" />
                      {/* <div className="na-circle na-c3" /> */}
                      <p className="na-stat-label">Resolved Today</p>
                      <h2 className="na-stat-value">{stats.resolvedToday}</h2>
                    </div>

                    <div className="na-stat-card na-warnings">
                      <div className="na-circle na-c1" />
                      <div className="na-circle na-c2" />
                      {/* <div className="na-circle na-c3" /> */}
                      <p className="na-stat-label">Warnings</p>
                      <h2 className="na-stat-value">{stats.warnings}</h2>
                    </div>

                    <div className="na-stat-card na-info">
                      <div className="na-circle na-c1" />
                      <div className="na-circle na-c2" />
                      {/* <div className="na-circle na-c3" /> */}
                      <p className="na-stat-label">Info Notices</p>
                      <h2 className="na-stat-value">{String(stats.infoNotices).padStart(2, '0')}</h2>
                    </div>
                  </div>
                </div>

                {/* Notification panel */}
                <div className="na-notifications-panel scrollbar">
                  <div className="na-notifications-header">
                    <p className="na-section-label" style={{ marginBottom: 0 }}>ALL ALERTS</p>
                    <div className="na-tabs">
                      {(['All', 'Critical', 'Warning', 'Resolved'] as TabType[]).map(tab => (
                        <button
                          key={tab}
                          className={`na-tab ${activeTab === tab ? 'active' : ''}`}
                          onClick={() => setActiveTab(tab)}
                        >
                          {tab}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="na-notifications-list">
                    {filteredNotifications.length === 0 ? (
                      <div className="na-notifications-empty">No alerts found</div>
                    ) : (
                      filteredNotifications.map(alert => (
                        <div key={alert.alertId} className="na-notification-item">
                          <div className="na-notification-info">
                            <p className="na-notification-name">
                              {alert.alertName} — {alert.deviceName}
                            </p>
                            <p className="na-notification-desc">
                              {alert.alertConditions?.length > 0
                                ? `${alert.alertConditions[0].pollutantParameterName} ${alert.alertConditions[0].operator} ${alert.alertConditions[0].thresholdValue} ${alert.alertConditions[0].parameterUnitName}`
                                : 'No conditions configured'}
                            </p>
                          </div>
                          <div className="na-notification-meta">
                            <span className={`na-severity-badge na-sev-${alert.severity.toLowerCase()}`}>
                              {alert.severity}
                            </span>
                            <span className="na-notification-time">
                              {alert.lastTriggeredAt ? getTimeAgo(alert.lastTriggeredAt) : '--'}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── Alerts List ───────────────────────────────────────────── */}
            <div className="na-list-section">
              {screenSize > 860 && <p className="na-section-label zoomOutLevel">ALERTS LIST</p>}
              {screenSize > 860 ? (
                <AlertsTable
                  alerts={filteredAlerts}
                  onEdit={handleEditClick}
                  onDelete={handleDeleteClick}
                  onStatusToggle={handleStatusToggle}
                  searchTerm={searchTerm}
                />
              ) : (
                renderMobileCards()
              )}
            </div>
          </>
        )}

        {/* Filter Modal */}
        {isFilterModalOpen && (
          <FilterModal
            isOpen={isFilterModalOpen}
            onClose={() => setIsFilterModalOpen(false)}
            filters={filters}
            onApplyFilters={setFilters}
          />
        )}

        {/* Delete Confirmation */}
        {isDeleteModalOpen && alertToDelete && (
          <ConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={() => {
              if (!isDeleting) {
                setIsDeleteModalOpen(false);
                setAlertToDelete(null);
              }
            }}
            onConfirm={handleDeleteConfirm}
            title="Delete Alert"
            message={`Are you sure you want to delete the alert "${alertToDelete.alertName}"? This action cannot be undone.`}
            confirmText={isDeleting ? 'Deleting...' : 'Delete'}
            cancelText="Cancel"
            type="danger"
            disabled={isDeleting}
          />
        )}

        <StatusMessage
          type={statusMessage.type}
          message={statusMessage.message}
          isVisible={statusMessage.isVisible}
          onClose={closeStatusMessage}
          autoHideDelay={4000}
        />

        {screenSize <= 860 && renderMobileActionMenu()}
      </div>

      {isCreateModalOpen && (
        <CreateAlertModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={handleCreateAlert}
        />
      )}

      {isEditModalOpen && selectedAlert && (
        <EditAlertModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onUpdate={handleEditAlert}
          alertData={selectedAlert as ApiAlertData}
        />
      )}
    </DashboardLayout>
  );
};

export default NewAlertsPage;
